<template>
  <DialogForm
    :model-value="modelValue"
    title="Programmer une relance"
    size="medium"
    :loading="saving"
    :save-disabled="!canConfirm"
    save-label="Enregistrer et terminer l'appel"
    saving-label="Enregistrement…"
    save-icon="fas fa-check"
    :dirty="dirty"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('update:modelValue', false)"
    @save="handleConfirm"
  >
    <Input :model-value="objet" label="Objet" disabled />

    <Input
      v-model="motif"
      list="relance-motifs"
      label="Motif précis"
      required
      placeholder="Ex : attente retour devis, validation budget, choix d'une date…"
      testId="relance-motif"
    />
    <datalist id="relance-motifs">
      <option v-for="s in motifSuggestions" :key="s" :value="s" />
    </datalist>

    <Input
      v-model="dueDateLocal"
      type="datetime-local"
      label="Date et heure"
      required
      :min="minDateTime"
      :max="maxDateTime"
      :hint="dateHint"
      :error="dateError"
      testId="relance-date"
    />

    <div class="form-group">
      <label id="relance-priorite-label">
        Priorité <span class="required-indicator" aria-label="requis">*</span>
      </label>
      <!-- Sélecteur coloré par température (froid → très chaud) : aucun champ
           du kit ne porte une couleur par option, et c'est ce qui rend la
           priorité lisible d'un coup d'œil. Reste local en connaissance de
           cause. -->
      <div class="priority-options" role="group" aria-labelledby="relance-priorite-label">
        <button
          v-for="opt in priorityOptions"
          :key="opt.value"
          type="button"
          class="priority-btn"
          :class="{ selected: priorite === opt.value }"
          :style="priorite === opt.value ? selectedStyle(opt.value) : ''"
          @click="priorite = opt.value"
          :data-testid="`relance-priorite-${opt.value}`"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <Checkbox v-model="notifyClient" data-testid="relance-notify-client">
      Prévenir le client par email
    </Checkbox>

    <Button
      variant="ghost"
      size="sm"
      icon="fas fa-circle-info"
      class="relance-hint"
      data-testid="relance-process-info"
      @click="showProcessInfo = true"
    >
      Que se passe-t-il après l'enregistrement ?
    </Button>
  </DialogForm>

  <InfoModal
    v-model="showProcessInfo"
    title="Programmer une relance : que se passe-t-il ensuite ?"
    size="large"
    @close="showProcessInfo = false"
  >
    <RelanceProcessInfo />
  </InfoModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Button, Checkbox, DialogForm, Input, InfoModal } from '../ui'
import { useDialogGuardOn } from '../../composables/ui/useDialogGuard.js'
import RelanceProcessInfo from '../shared/RelanceProcessInfo.vue'
import {
  LeadPriority,
  LEAD_PRIORITY_OPTIONS,
  LEAD_PRIORITY_COLORS,
  RELANCE_MOTIF_SUGGESTIONS,
  RELANCE_DEFAULT_OBJET
} from '../../constants/commercial-call.js'
import { toDatetimeLocalValue } from '../../utils/dateFormatters.js'
import { devisEnvoye, relanceMaxDayLabel, relanceTropTardive, relanceImpossible, formatDayLabelFr } from '../../utils/relanceBorne.js'
import { QUOTE_AUTO_LOSS_DAYS, QUOTE_RELANCE_MAX_DAYS } from '@shared/quote-relance.js'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  saving: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const objet = RELANCE_DEFAULT_OBJET
const motifSuggestions = RELANCE_MOTIF_SUGGESTIONS
const priorityOptions = LEAD_PRIORITY_OPTIONS

// Relance interdite dans le passé, et au-delà de la veille du passage
// automatique en « Devis perdu » (le serveur rejoue la même borne).
const dernierJour = ref('')
const maxDateTime = computed(() => (dernierJour.value ? `${dernierJour.value}T23:59` : ''))

const motif = ref('')
const dueDateLocal = ref('')
const priorite = ref(LeadPriority.TIEDE)
const notifyClient = ref(true)
const showProcessInfo = ref(false)
const minDateTime = ref('')

const tropTard = computed(() => relanceTropTardive(dueDateLocal.value, dernierJour.value))
const impossible = computed(() => relanceImpossible(dernierJour.value))
const dateHint = computed(() => {
  if (!dernierJour.value) return ''
  if (impossible.value) return `Ce devis passe aujourd'hui en « Devis perdu », ${QUOTE_AUTO_LOSS_DAYS} jours après son envoi : une relance ne peut plus être programmée.`
  const limite = `Au plus tard le ${formatDayLabelFr(dernierJour.value)}`
  return devisEnvoye(props.quote)
    ? `${limite} : sans signature, le devis passe en « Devis perdu » ${QUOTE_AUTO_LOSS_DAYS} jours après son envoi.`
    : `${limite}, soit ${QUOTE_RELANCE_MAX_DAYS} jours à partir d'aujourd'hui.`
})
const dateError = computed(() => (tropTard.value && !impossible.value ? `Choisissez une date au plus tard le ${formatDayLabelFr(dernierJour.value)}.` : ''))

const canConfirm = computed(() => Boolean(motif.value.trim() && dueDateLocal.value && priorite.value && !tropTard.value && !impossible.value))

const selectedStyle = (value) => {
  const c = LEAD_PRIORITY_COLORS[value]
  if (!c) return ''
  return `background:${c.bg};color:${c.color};border-color:${c.color}`
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      motif.value = ''
      dueDateLocal.value = ''
      priorite.value = LeadPriority.TIEDE
      notifyClient.value = true
      minDateTime.value = toDatetimeLocalValue(new Date())
      dernierJour.value = relanceMaxDayLabel(props.quote)
    }
  }
)

// Empreinte du formulaire pour la garde de fermeture (M21) : reprise à chaque
// ouverture, juste après la remise à zéro des champs ci-dessus (le watcher est
// déclaré après, donc rejoué après elle sur le même changement de `modelValue`).
const formSnapshot = computed(() => ({
  motif: motif.value,
  dueDateLocal: dueDateLocal.value,
  priorite: priorite.value,
  notifyClient: notifyClient.value
}))
const { dirty } = useDialogGuardOn(
  computed(() => props.modelValue),
  formSnapshot
)

const handleConfirm = () => {
  if (!canConfirm.value) return
  emit('confirm', {
    motif: motif.value.trim(),
    dueDate: new Date(dueDateLocal.value).toISOString(),
    priorite: priorite.value,
    notifyClient: notifyClient.value
  })
}
</script>

<style scoped>
/* Sélecteur de priorité coloré par température : propre à cette fenêtre, cf.
   commentaire dans le template. */
.priority-options {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.priority-btn {
  flex: 1;
  min-width: 90px;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
}

.priority-btn:hover {
  border-color: var(--color-primary);
}

.priority-btn.selected {
  font-weight: 700;
}

.relance-hint {
  align-self: flex-start;
}
</style>
